<main id="main-content" class="h-screen flex flex-col pl-8 py-4 pr-4 transition-all duration-0">
    <div class="w-full flex flex-col p-2 items-center gap-2">
        <div class="w-full flex flex-row items-center justify-between">
            <div class="w-full flex gap-2 items-center justify-start">
                <h1 class="oswald-font text-3xl uppercase">
                    Threats & Hazards
                </h1>
                <div data-popover-target="popover-right" data-popover-placement="right" type="button">
                    <lucide-icon [name]="icons.CircleHelp" [size]=24 class="text-red-500"></lucide-icon>
                </div>
                <div data-popover id="popover-right" role="tooltip" class="absolute z-10 invisible inline-block w-[600px] text-sm text-gray-500 transition-opacity duration-300 bg-white border border-gray-200 rounded-md shadow-xs opacity-0">
                    <div class="px-3 py-2">
                        <p>
                            Complete the section below to identify all threats and hazards that your institution faces. Fill out the information to conduct your <strong>risk and vulnerability assessment</strong>.
                        </p>
                        <p>
                            To add a new threat/hazard, click on 
                            <span class="inline-flex px-3 py-1 h-full flex flex-row gap-2 items-center justify-center rounded-md bg-gradient-to-b from-nside-orange-300 to-nside-orange-400 text-white oswald-font">  
                                <lucide-icon [img]="icons.Plus"></lucide-icon>
                                Add New
                            </span>. In the pop-up, fill out the <strong>risk and hazard vulnerability assessment</strong> for the threat/hazard. When completed, click on 
                            <span class="inline-flex px-3.5 py-1 flex items-center justify-center text-white oswald-font bg-gradient-to-b from-nside-orange-300 to-nside-orange-400 rounded-md">
                                Save
                            </span>.
                        </p>
                        <p>
                            To edit a <strong>risk and vulnerability assessment</strong> for a threat/hazard, click on 
                            <span class="inline-flex flex items-center justify-center px-3 h-8 font-normal text-white oswald-font border border-nside-orange-300 bg-gradient-to-b from-nside-orange-300 to-nside-orange-400 rounded-md"
                            >
                                Edit
                            </span>. When finished editing, ensure that you save your progress.
                        </p>
                        <p>
                            To delete a threat/hazard <strong>risk and vulnerability assessment</strong>, click on 
                            <span class="inline-flex flex items-center justify-center px-3 h-8 font-normal text-white oswald-font border border-nside-gray-300 bg-gradient-to-b from-nside-gray-300 to-nside-gray-400 rounded-md"
                            >
                                Delete
                            </span>. Once a threat/hazard has been removed, it will be removed from all sections including Step 3 and Step 4. 
                        </p>
                        <p>
                            Once all threats/hazards have been identified, navigate to the <strong>Functions</strong> section.
                        </p>
                    </div>
                    <div data-popper-arrow></div>
                </div>
            </div>
            <eop-assistant
                [sectionId]="sectionType"
            ></eop-assistant>         
        </div>
    </div>
    
    <!-- MAIN CONTENT -->
    <div class="p-2 w-full flex flex-grow flex-col items-start gap-8 overflow-y-auto">
        <div class="max-h-full w-full flex flex-col gap-4">
            <div class="h-full w-full bg-white rounded-md shadow-custom-2 flex flex-col items-center justify-center p-6 gap-4">
                <div class="w-full flex items-center justify-between">
                    <h3 class="oswald-font text-xl font-medium">Threat or Hazard</h3>
                    <button 
                        *ngIf="canSave()"
                        (click)="$event.preventDefault(); showAddNewThreat = true;"
                        class="px-4 py-2 h-full flex flex-row gap-2 items-center justify-center px-4 py-2 rounded-md bg-gradient-to-b from-nside-orange-300 to-nside-orange-400  hover:bg-gradient-to-b hover:from-nside-orange-100 hover:to-nside-orange-200 text-white oswald-font"
                    >  
                        <lucide-icon [img]="icons.Plus"></lucide-icon>
                        Add New
                    </button>
                </div>
                <div class="w-full h-full flex flex-col itesm-center divide-y overflow-y-auto">
                    <ng-container *ngIf="threatsList.length > 0; else emptyThreat">
                        <div 
                            class="w-full flex flex-wrap items-start justify-between py-2"
                            *ngFor="let threat of threatsList"
                        >
                            <p class="max-w-[70%] whitespace-pre-line break-words overflow-hidden text-start">
                                {{ threat.threat }}
                            </p>
                            <div class="w-full max-w-[30%] flex items-center justify-end gap-2">
                                <button 
                                    (click)="$event.preventDefault(); handleOpenModal('threat-details', threat);"
                                    class="w-full max-w-20 py-2 font-normal oswald-font border border-nside-gray-400 bg-white rounded-md hover:bg-gray-100 cursor-pointer"
                                >
                                    Preview
                                </button>
                                <button 
                                    *ngIf="canSave()"
                                    (click)="$event.preventDefault(); handleOpenModal('edit-threat', threat);"                                
                                    class="w-full max-w-20 py-2 font-normal text-white oswald-font border border-nside-orange-300 bg-gradient-to-b from-nside-orange-300 to-nside-orange-400 rounded-md hover:bg-gradient-to-b hover:from-nside-orange-100 hover:to-nside-orange-200 cursor-pointer"
                                >
                                    Edit
                                </button>
                                <button 
                                    *ngIf="canSave()"
                                    (click)="$event.preventDefault(); handleOpenModal('delete-confirmation', threat);"
                                    class="w-full max-w-20 py-2 font-normal text-white oswald-font border border-nside-gray-300 bg-gradient-to-b from-nside-gray-300 to-nside-gray-400 rounded-md hover:bg-gradient-to-b hover:from-nside-gray-100 hover:to-nside-gray-200 cursor-pointer"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </ng-container>
                    <ng-template #emptyThreat>
                        <p class="w-full text-nside-gray-100 italic">
                            Empty list
                        </p>
                    </ng-template>
                </div>
            </div>
        </div>
    </div>

    <introduction></introduction>

    <threat-details
        *ngIf="showThreatDetails"
        [threatDetails]="selectedThreat"
        (onClose)="handleCloseModal('threat-details')"
    ></threat-details>

    <new-threat
        *ngIf="showAddNewThreat"
        (onClose)="handleCloseModal('new-threat')"
        (onSave)="handleSave($event, 'new-threat')"
    ></new-threat>

    <threat-edit
        *ngIf="showEditThreat"
        [threatToEdit]="selectedThreat"
        (onClose)="handleCloseModal('edit-threat')"
        (onSave)="handleSave($event, 'edit-threat')"
    ></threat-edit>

    <delete-confirmation
        *ngIf="showDeleteConfirmation"
        [text]="'this threat'"
        [itemToDelete]="selectedThreat"
        (onClose)="handleCloseModal('delete-confirmation')" 
        (onDelete)="handleDelete($event)"
    ></delete-confirmation>

    <prv-next-buttons [totalPages]="(eopNavigationService.totalPages$ | async) || []" />

    <!-- Spinner -->
    <div *ngIf="loading">
        <spinner></spinner>
    </div>

    <popup-message 
        [type]="messageModal"
        [message]="popupMessage"
        (close)="resetMessageModal()"
    ></popup-message>
</main>